import { Box, Stack, Typography } from '@mui/material'
import { Message } from 'ai'
import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { buildActionSignature } from '@/features/copilot/confirmationPayload'
import ProposedActionCard, {
  ProposedAction,
  isProposedAction,
} from '@/features/copilot/ProposedActionCard'
import RecommendationCard, {
  RECOMMENDATION_TOOL_NAMES,
} from '@/features/copilot/RecommendationCard'

interface MessageBubbleProps {
  message: Message
  onConfirmAction?: (action: ProposedAction) => void
  onApplyRecommendation?: (instruction: string) => void
  disableActions?: boolean
  confirmedSignatures?: Set<string>
}

// CONFIRM_TOOL messages are machine-generated follow-ups produced by the
// widget when the user clicks Confirm on a proposed action. They carry the
// confirmation payload for the backend and should not be rendered in the
// chat feed (the human-friendly summary already lives in the warning card
// that produced them).
const isConfirmationFollowUp = (message: Message): boolean => {
  if (message.role !== 'user') return false
  const text =
    typeof message.content === 'string' ? message.content : ''
  return text.trim().startsWith('CONFIRM_TOOL')
}

const collectProposedActions = (message: Message): ProposedAction[] => {
  const actions: ProposedAction[] = []
  const invocations = message.toolInvocations ?? []
  for (const inv of invocations) {
    if (inv.state !== 'result') continue
    const result = (inv as { result?: unknown }).result
    if (isProposedAction(result)) {
      actions.push({
        ...result,
        toolCallId: result.toolCallId ?? inv.toolCallId,
      })
    }
  }
  return actions
}

const UNAVAILABLE_TOOL_PATTERN =
  /Model tried to call unavailable tool|unavailable tool|tool .* not found/i

const sanitizeContent = (raw: unknown): string => {
  if (typeof raw !== 'string') return ''
  if (UNAVAILABLE_TOOL_PATTERN.test(raw)) {
    return 'Câu hỏi này tôi chưa xử lý được. Bạn vui lòng hỏi theo cách khác giúp tôi.'
  }
  return raw
}

const MessageBubble = ({
  message,
  onConfirmAction,
  onApplyRecommendation,
  disableActions = false,
  confirmedSignatures,
}: MessageBubbleProps) => {
  if (isConfirmationFollowUp(message)) return null

  const isUser = message.role === 'user'
  const proposedActions = collectProposedActions(message)
  const displayContent = isUser
    ? typeof message.content === 'string'
      ? message.content
      : ''
    : sanitizeContent(message.content)

  const nonProposedToolNames = (message.toolInvocations ?? [])
    .filter((inv) => {
      if (inv.state !== 'result') return true
      const result = (inv as { result?: unknown }).result
      if (isProposedAction(result)) return false
      if (
        typeof inv.toolName === 'string' &&
        RECOMMENDATION_TOOL_NAMES.has(inv.toolName)
      ) {
        return false
      }
      return true
    })
    .map((inv) => inv.toolName)
    .filter((name) => typeof name === 'string' && !UNAVAILABLE_TOOL_PATTERN.test(name))

  // Recommendation tool results are rendered as dedicated cards
  // (F1-F5 from draft-copilot-recommendation-fe.md). Exclude them from the
  // generic "Using tool: X" label so users don't see the same tool name
  // twice for the same tool invocation.
  const recommendationInvocations = (message.toolInvocations ?? []).filter(
    (inv) =>
      inv.state === 'result' &&
      typeof inv.toolName === 'string' &&
      RECOMMENDATION_TOOL_NAMES.has(inv.toolName),
  )

  const toolCallLabel = nonProposedToolNames.length
    ? `Using tool: ${nonProposedToolNames.join(', ')}`
    : null

  return (
    <Box
      sx={{
        alignSelf: isUser ? 'flex-end' : 'flex-start',
        maxWidth: '88%',
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
      }}
    >
      {toolCallLabel && (
        <Typography
          variant="caption"
          sx={{
            color: 'grey.600',
            fontStyle: 'italic',
            px: 0.5,
          }}
        >
          {toolCallLabel}
        </Typography>
      )}
      {displayContent && (
        <Box
          sx={{
            px: 1.5,
            py: 1,
            borderRadius: 2,
            bgcolor: isUser ? '#004744' : 'background.overrideDefault',
            border: '1px solid',
            borderColor: isUser ? 'transparent' : 'border.main',
            color: isUser ? '#fff' : 'text.main',
            fontSize: 13.5,
            lineHeight: 1.55,
            // Wrap long content (including URLs, tokens, stack traces) rather
            // than clipping or forcing a horizontal scroll on the bubble.
            wordBreak: 'break-word',
            overflowWrap: 'anywhere',
            whiteSpace: 'normal',
            '& p': { m: 0, whiteSpace: 'pre-wrap' },
            '& p + p': { mt: 0.75 },
            '& ul, & ol': { pl: 2.25, my: 0.5 },
            '& li + li': { mt: 0.25 },
            '& code': {
              px: 0.5,
              py: 0.1,
              borderRadius: 0.5,
              fontSize: 12.5,
              bgcolor: isUser ? 'rgba(255,255,255,0.18)' : 'action.hover',
              fontFamily: 'monospace',
            },
            '& pre': {
              m: 0,
              my: 0.5,
              p: 1,
              borderRadius: 1,
              bgcolor: isUser ? 'rgba(255,255,255,0.12)' : 'action.hover',
              overflowX: 'auto',
              fontSize: 12.5,
            },
            '& pre code': { p: 0, bgcolor: 'transparent' },
            '& a': {
              color: isUser ? '#fff' : 'primary.main',
              textDecoration: 'underline',
            },
            '& h1, & h2, & h3, & h4': {
              m: 0,
              mt: 0.5,
              fontSize: 14,
              fontWeight: 600,
            },
            '& blockquote': {
              m: 0,
              pl: 1,
              borderLeft: '3px solid',
              borderColor: isUser ? 'rgba(255,255,255,0.4)' : 'border.main',
              opacity: 0.9,
            },
            '& table': {
              borderCollapse: 'collapse',
              my: 0.5,
              fontSize: 12.5,
              display: 'block',
              overflowX: 'auto',
              maxWidth: '100%',
            },
            '& th, & td': {
              border: '1px solid',
              borderColor: isUser ? 'rgba(255,255,255,0.3)' : 'border.main',
              px: 0.75,
              py: 0.4,
              textAlign: 'left',
              verticalAlign: 'top',
            },
            '& th': {
              fontWeight: 600,
              bgcolor: isUser ? 'rgba(255,255,255,0.1)' : 'action.hover',
            },
          }}
        >
          {isUser ? (
            <Typography
              variant="body2"
              component="div"
              sx={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                overflowWrap: 'anywhere',
                fontSize: 'inherit',
                lineHeight: 'inherit',
                color: 'inherit',
              }}
            >
              {displayContent}
            </Typography>
          ) : (
            <Markdown remarkPlugins={[remarkGfm]}>{displayContent}</Markdown>
          )}
        </Box>
      )}
      {proposedActions.length > 0 && onConfirmAction && (
        <Stack spacing={1} sx={{ mt: 0.5 }}>
          {proposedActions.map((action, idx) => {
            const signature = buildActionSignature(
              action.action,
              action.args as Record<string, unknown>,
            )
            const alreadyConfirmed =
              confirmedSignatures?.has(signature) ?? false
            return (
              <ProposedActionCard
                key={action.toolCallId ?? `${action.action}-${idx}`}
                action={action}
                onConfirm={onConfirmAction}
                disabled={disableActions}
                alreadyConfirmed={alreadyConfirmed}
              />
            )
          })}
        </Stack>
      )}
      {recommendationInvocations.length > 0 && (
        <Stack spacing={1} sx={{ mt: 0.5 }}>
          {recommendationInvocations.map((inv) => (
            <RecommendationCard
              key={inv.toolCallId}
              toolName={inv.toolName as string}
              result={(inv as { result?: unknown }).result}
              onApplyRecommendation={onApplyRecommendation}
              disableActions={disableActions}
            />
          ))}
        </Stack>
      )}
    </Box>
  )
}

export default MessageBubble
